import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MapPin } from 'lucide-react';
import { asset } from '@/lib/api';
import { SectionHeading } from '@/components/SectionHeading';
import { MagneticButton } from '@/components/MagneticButton';
import { useMediaQuery } from '@/lib/hooks';

export const ProjectsShowcase = ({ projects = [] }) => {
  const list = projects.filter((p) => !p.placeholder && !p.ongoing).slice(0, 6);
  const [active, setActive] = useState(null);
  const previewRef = useRef(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const onMove = (e) => {
    if (!previewRef.current) return;
    previewRef.current.style.transform = `translate(${e.clientX + 24}px, ${e.clientY - 120}px)`;
  };

  return (
    <section className='relative bg-[#20272A]' onMouseMove={isDesktop ? onMove : undefined}>
      <div className='grain absolute inset-0' />
      <div className='relative container-x py-20 lg:py-28'>
        <div className='flex flex-col justify-between gap-6 md:flex-row md:items-end'>
          <SectionHeading eyebrow='Selected Projects' title='Projects that moved the ground forward.' light />
          <MagneticButton to='/projects' variant='secondary' className='shrink-0 text-[#F6F5F1]' data-cursor='VIEW'>
            All Projects
          </MagneticButton>
        </div>

        <div className='mt-12 border-t border-white/10' data-testid='projects-editorial-list'>
          {list.map((p, i) => (
            <Link
              key={p.slug}
              to={`/projects/${p.slug}`}
              data-testid='project-row'
              data-cursor='VIEW PROJECT'
              onMouseEnter={() => setActive(p)}
              onMouseLeave={() => setActive(null)}
              className='group relative flex items-center gap-6 border-b border-white/10 py-7 transition-colors duration-300 hover:bg-white/[0.03]'
            >
              <span className='font-mono-num w-10 shrink-0 text-sm text-[#B88A3B]'>{String(i + 1).padStart(2, '0')}</span>

              {/* mobile thumbnail */}
              <img src={asset(p.heroImage)} alt='' className='h-16 w-24 shrink-0 rounded-lg object-cover lg:hidden' />

              <div className='min-w-0 flex-1'>
                <h3 className='truncate font-head text-2xl font-semibold text-[#F6F5F1] transition-colors duration-300 group-hover:text-[#B88A3B] sm:text-3xl'>
                  {p.associatedBrand || p.projectTitle}
                </h3>
                <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#F6F5F1]/55'>
                  <span className='inline-flex items-center gap-1.5'><MapPin size={14} className='text-[#B88A3B]' /> {p.location}</span>
                  <span className='hidden sm:inline text-white/20'>|</span>
                  <span>{(p.serviceCategory || []).join(', ')}</span>
                </div>
              </div>

              <span className='hidden shrink-0 items-center justify-center text-[#F6F5F1]/40 transition-all duration-300 group-hover:text-[#B88A3B] sm:flex'>
                <ArrowUpRight size={26} className='transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1' />
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* cursor-follow preview (desktop) */}
      {isDesktop && (
        <div
          ref={previewRef}
          className='pointer-events-none fixed left-0 top-0 z-30 hidden lg:block'
          style={{ transition: 'opacity 0.3s ease' }}
        >
          <div
            className='h-56 w-80 overflow-hidden rounded-xl border border-[#B88A3B]/30 shadow-2xl transition-opacity duration-300'
            style={{ opacity: active ? 1 : 0 }}
          >
            {active && <img src={asset(active.heroImage)} alt='' className='h-full w-full object-cover' />}
          </div>
        </div>
      )}
    </section>
  );
};
